import { Controller } from "@hotwired/stimulus"

/**
 * Variant Status Poll Controller
 *
 * Polls the variant status API when viewing a product that is still processing.
 * When the variant becomes "finished", refreshes the page so that Embed and
 * Customize links (and other status-dependent actions) become enabled.
 *
 * Supports single variant (product page) or multiple variants (project page):
 * - Single: data-variant-status-poll-variant-uuid-value="<uuid>"
 *           data-variant-status-poll-initial-status-value="<image_status>"
 * - Multiple: data-variant-status-poll-variant-uuids-value='["uuid1","uuid2"]'
 *             (polls each; refreshes page when any becomes "finished")
 */
export default class VariantStatusPollController extends Controller {
  static values = {
    variantUuid: String,
    variantUuids: Array,
    initialStatus: String
  }

  // Statuses that mean we should keep polling (variant is still processing)
  static PROCESSING_STATUSES = [
    "initial",
    "created",
    "uploaded",
    "uploading",
    "validating",
    "validated",
    "processing",
    "overdue",
    "rendering",
    "upscaling",
    "upscaled",
    "cloned",
    "cloning",
    "remeasuring",
    "reverting"
  ]

  connect() {
    const uuids = this.variantUuidsValue?.filter(Boolean) || []
    const singleUuid = this.variantUuidValue || ""
    const status = this.initialStatusValue || ""

    if (uuids.length > 0) {
      // Project page: poll multiple variants
      this.pollInterval = 10000
      this.pollCount = 0
      this.maxPolls = 180
      this.variantUuidsToPoll = uuids
      this.schedulePoll()
      return
    }

    if (!singleUuid || !this.shouldPoll(status)) {
      return
    }

    // Single variant (product page)
    this.pollInterval = 10000
    this.pollCount = 0
    this.maxPolls = 180
    this.variantUuidsToPoll = [singleUuid]
    this.schedulePoll()
  }

  disconnect() {
    this.stopPolling()
  }

  shouldPoll(status) {
    return status && this.constructor.PROCESSING_STATUSES.includes(status)
  }

  schedulePoll() {
    this.pollTimer = setTimeout(() => this.poll(), this.pollInterval)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  async poll() {
    if (this.pollCount >= this.maxPolls || !this.variantUuidsToPoll?.length) {
      this.stopPolling()
      return
    }

    this.pollCount++

    try {
      const results = await Promise.all(
        this.variantUuidsToPoll.map((uuid) =>
          fetch(`/api/v2/products/${uuid}/status`).then((r) => r.json())
        )
      )

      const anyFinished = results.some(
        (data) => data?.variant?.image_status === "finished"
      )

      if (anyFinished) {
        this.stopPolling()
        Turbo.visit(window.location.href, { action: "replace" })
      } else {
        this.schedulePoll()
      }
    } catch (_) {
      this.schedulePoll()
    }
  }
}
